import {
  AXIS_QUESTIONS,
  BETTER_VARIANT_OPTIONS,
  BOARD_QUESTIONS,
  VARIANT_VERDICT_QUESTION,
  type AxisAnswer,
  type BoardAnswer,
  type Option,
} from '@cf/shared'

/**
 * What this reviewer already said, shown back to them on the report.
 *
 * The report is where they scroll to see the whole profile; without this they
 * would have to open each piece of evidence to remember what they answered
 * about it. Labels come from the question definitions, so the summary cannot
 * drift from the form that produced it.
 */
function labelOf(options: readonly Option[], value: string | null | undefined) {
  return options.find((option) => option.value === value)?.label ?? null
}

function Panel({
  title,
  parts,
  comment,
}: {
  title: string
  parts: Array<{ id: string; prompt: string; label: string }>
  comment: string | null | undefined
}) {
  if (parts.length === 0 && !comment) return null
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-navy-light bg-navy-light/40 px-4 py-3">
      <span className="text-[10px] font-semibold tracking-[0.08em] text-navy-dark uppercase">
        {title}
      </span>
      <dl className="flex flex-col gap-0.5 text-xs">
        {parts.map((part) => (
          <div key={part.id} className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">{part.prompt}</dt>
            <dd className="font-semibold">{part.label}</dd>
          </div>
        ))}
      </dl>
      {comment ? (
        <p className="border-t border-navy-light pt-2 font-serif text-sm leading-relaxed text-ink-soft">
          {comment}
        </p>
      ) : null}
    </div>
  )
}

export function BoardAnswerSummary({
  answer,
  wordings,
}: {
  answer: BoardAnswer
  /** Screen labels of the wordings, so "wersja A" means the same here. */
  wordings?: Array<{ variant: string; label: string }>
}) {
  const perVariant = (wordings ?? []).flatMap((wording) => {
    const label = labelOf(
      VARIANT_VERDICT_QUESTION.options,
      answer.variants?.[wording.variant],
    )
    return label
      ? [{ id: wording.variant, prompt: `Wersja ${wording.label}`, label }]
      : []
  })
  const better = labelOf(BETTER_VARIANT_OPTIONS, answer.better)
  return (
    <Panel
      title="Ocena"
      comment={answer.comment}
      parts={[
        ...perVariant,
        ...(better ? [{ id: 'better', prompt: 'Lepszy opis', label: better }] : []),
        ...BOARD_QUESTIONS.map((question) => {
          const label = labelOf(question.options, answer[question.id])
          return label ? { id: question.id, prompt: question.prompt, label } : null
        }).filter((part) => part !== null),
      ]}
    />
  )
}

export function AxisAnswerSummary({ answer }: { answer: AxisAnswer }) {
  return (
    <Panel
      title="Twoje uwagi do tej osi"
      comment={answer.comment}
      parts={AXIS_QUESTIONS.map((question) => {
        const label = labelOf(question.options, answer[question.id])
        return label ? { id: question.id, prompt: question.prompt, label } : null
      }).filter((part) => part !== null)}
    />
  )
}
